import {
  ADDITIONAL_PROTECTION_GROUP_ID,
  COMFORT_VK_DEDUCTIBLE,
  FALLBACK_TK_DEDUCTIBLE,
  FALLBACK_VK_DEDUCTIBLE,
  INSURANCE_UPGRADE_GROUP_ID,
  OLD_DRIVER_ADDON_ID,
  ONE_TIME_MILEAGE_GROUP_ID,
  YOUNG_DRIVER_ADDON_ID,
} from '@finn/ua-constants';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';

import { Deal } from '../hooks/useDeal';
import { AddonsResponse } from '../hooks/useGetAddons';
import { Addon } from '../types/addons';

export const PREMIUM_DEDUCTIBLE_VALUE = 500;
export const PREMIUM_DEDUCTIBLE_VALUE_IN_CENTS = 50000;

export const hasYoungOrOldDriverInAddons = (addonsResponse: AddonsResponse) => {
  return (
    addonsResponse?.appliedAddonIds.includes(YOUNG_DRIVER_ADDON_ID) ||
    addonsResponse?.appliedAddonIds.includes(OLD_DRIVER_ADDON_ID)
  );
};

export const getAdditionalProtectionAddonAmount = (
  addonsResponse: AddonsResponse,
  targetAddonId: string
) => {
  const hasAddon = addonsResponse?.appliedAddonIds?.includes(targetAddonId);
  if (!hasAddon) return undefined;

  const appliedAddons = addonsResponse?.appliedAddons;
  const addonGroup = appliedAddons.groups?.find(
    (group) => group.group.group_id === ADDITIONAL_PROTECTION_GROUP_ID
  );
  if (!addonGroup) return undefined;
  const targetAddon = addonGroup?.addons.find(
    (addon) => addon.addon_id === targetAddonId
  );
  if (!targetAddon) return undefined;

  return targetAddon?.pricing?.amount?.gross;
};

export const getInsuranceAddons = (addonsResponse: AddonsResponse) => {
  const insuranceAddonGroup = addonsResponse?.allAddons?.groups.find(
    (group) => group.group.group_id === INSURANCE_UPGRADE_GROUP_ID
  );

  return insuranceAddonGroup?.addons;
};

export const getOneTimeMileageAddons = (addonsResponse: AddonsResponse) => {
  const oneTimeMileageAddonGroup = addonsResponse?.allAddons?.groups.find(
    (group) => group.group.group_id === ONE_TIME_MILEAGE_GROUP_ID
  );

  return oneTimeMileageAddonGroup?.addons;
};

export const getAppliedOneTimeMileageAddon = (
  addonsResponse: AddonsResponse | undefined
) => {
  const oneTimeMileageAddonGroup = addonsResponse?.appliedAddons?.groups.find(
    (group) => group.group.group_id === ONE_TIME_MILEAGE_GROUP_ID
  );

  return oneTimeMileageAddonGroup?.addons?.[0];
};

export const getAdditionalProtectionAddons = (
  addonsResponse: AddonsResponse
) => {
  const protectionAddonGroup = addonsResponse?.allAddons?.groups.find(
    (group) => group.group.group_id === ADDITIONAL_PROTECTION_GROUP_ID
  );

  return protectionAddonGroup?.addons || [];
};

export const getAppliedAdditionalProtectionAddons = (
  addonsResponse: AddonsResponse
) => {
  const protectionAddonGroup = addonsResponse?.appliedAddons?.groups.find(
    (group) => group.group.group_id === ADDITIONAL_PROTECTION_GROUP_ID
  );

  return protectionAddonGroup?.addons || [];
};

export const getAppliedInsuranceAddon = (
  addonsResponse: AddonsResponse | undefined
) => {
  const insuranceAddonGroup = addonsResponse?.appliedAddons?.groups.find(
    (group) => group.group.group_id === INSURANCE_UPGRADE_GROUP_ID
  );

  return insuranceAddonGroup?.addons?.[0];
};

export const getAddonTitle = (addon?: Addon) => {
  if (!addon) return '';

  return addon?.display_info?.title;
};

export const canUpgradeInsuranceWithAddons = (
  addonsResponse: AddonsResponse | undefined
) => {
  if (!addonsResponse) return false;
  const hasGroupId = addonsResponse?.availableGroupIds.includes(
    INSURANCE_UPGRADE_GROUP_ID
  );

  const insuranceAddons = getInsuranceAddons(addonsResponse);

  const mostExpensiveInsuranceObject = insuranceAddons?.reduce(
    (max, addon) =>
      addon.pricing.amount.gross > max.pricing.amount.gross ? addon : max,
    insuranceAddons?.[0]
  );

  const hasMostExpensiveInsurance = mostExpensiveInsuranceObject?.addon_id
    ? addonsResponse?.appliedAddonIds.includes(
        mostExpensiveInsuranceObject?.addon_id
      )
    : false;

  return hasGroupId || (!hasGroupId && !hasMostExpensiveInsurance);
};

export const getCanAddAdditionalMileage = ({
  addonsResponse,
  deal,
}: {
  addonsResponse: AddonsResponse | undefined;
  deal: Deal | undefined;
}) => {
  if (!['CREATED', 'ACTIVE'].includes(deal?.state ?? '')) return false;

  const endDate = deal?.minimum_commitment_end_date;

  const endDateWithinTwoMonths = endDate
    ? dayjs(endDate).isBefore(dayjs().add(2, 'month'))
    : false;

  if (endDateWithinTwoMonths) return false;

  const alreadyHasOneTimeMileage = Boolean(
    getAppliedOneTimeMileageAddon(addonsResponse)
  );

  if (alreadyHasOneTimeMileage) return false;

  return true;
};

export const hasPremiumInsuranceInDeal = (deal: Deal | undefined) => {
  return (
    Number(deal?.damage_deductible_vollkasko) ===
      PREMIUM_DEDUCTIBLE_VALUE_IN_CENTS ||
    Number(deal?.customer_specific_vk_damage_deductible) ===
      PREMIUM_DEDUCTIBLE_VALUE
  );
};

export const getVkAndTkValues = (
  addonsResponse: AddonsResponse | undefined,
  deal: Deal | undefined
) => {
  const appliedInsuranceAddon = getAppliedInsuranceAddon(addonsResponse);
  const isPremiumInDeal = hasPremiumInsuranceInDeal(deal);

  const appliedInsuranceVkValue =
    appliedInsuranceAddon?.attributes?.coverage?.full;
  const appliedInsuranceTkValue =
    appliedInsuranceAddon?.attributes?.coverage?.partial;

  if (!isNil(appliedInsuranceVkValue) && !isNil(appliedInsuranceTkValue)) {
    return {
      vkValue: appliedInsuranceVkValue / 100,
      tkValue: appliedInsuranceTkValue / 100,
    };
  }

  const vkValueInDeal = deal?.damage_deductible_vollkasko;
  const tkValueInDeal = deal?.damage_deductible_teilkasko;

  if (vkValueInDeal && tkValueInDeal) {
    return {
      vkValue: vkValueInDeal / 100,
      tkValue: tkValueInDeal / 100,
    };
  }

  if (isPremiumInDeal) {
    return {
      vkValue: COMFORT_VK_DEDUCTIBLE,
      tkValue: FALLBACK_TK_DEDUCTIBLE,
    };
  }

  return {
    vkValue: FALLBACK_VK_DEDUCTIBLE,
    tkValue: FALLBACK_TK_DEDUCTIBLE,
  };
};
